import { useEffect, useState } from "react";
import { Headphones, Library, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PortalModuleToggle from "@/components/admin/PortalModuleToggle";
import AudienceSelector, { audienceLabel } from "@/components/admin/AudienceSelector";

const categories = {
  meditation_narration: "Meditasyon Anlatımı",
  meditation_music: "Meditasyon Müziği",
  guided_practice: "Rehberli Çalışma",
  training_narration: "Eğitim Anlatımı",
  tasavvuf_narration: "Tasavvuf Anlatımı",
  other_audio: "Diğer Ses İçeriği",
};
const emptyForm = { title: "", topic: "", description: "", audio_category: "meditation_narration", content_track: "pleiades", audience: "all", target_levels: [], client_id: "", file: null };

export default function AudioLibraryPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      const [audio, people] = await Promise.all([api.get("/audio-library"), api.get("/clients")]);
      setItems(audio.data); setClients(people.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ ...emptyForm, ...item, client_id: item.client_id || "", file: null });
    globalThis.scrollTo?.({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };
  const submit = async (event) => {
    event.preventDefault();
    if (!editingId && !form.file) return toast.error("Bir ses dosyası seçin");
    if (form.audience === "all" && !window.confirm("Bu içerik yeni gelen kullanıcılar dahil tüm portal kullanıcılarına açılacak. Devam edilsin mi?")) return;
    setBusy(true);
    try {
      const payload = new FormData();
      payload.append("title", form.title);
      payload.append("topic", form.topic);
      payload.append("description", form.description);
      payload.append("audio_category", form.audio_category);
      payload.append("content_track", form.content_track || "pleiades");
      payload.append("audience", form.audience);
      payload.append("target_levels", JSON.stringify(form.target_levels));
      if (form.client_id) payload.append("client_id", form.client_id);
      if (form.file) payload.append("file", form.file);
      if (editingId) await api.put(`/audio-library/${editingId}`, payload, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/audio-library", payload, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editingId ? "Ses içeriği güncellendi" : "Ses içeriği yayınlandı");
      setEditingId(null); setForm(emptyForm);
      event.target.reset();
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async (item) => {
    if (!window.confirm(`“${item.title}” silinsin mi?`)) return;
    try {
      await api.delete(`/audio-library/${item.id}`);
      setItems((current) => current.filter((row) => row.id !== item.id));
      toast.success("Ses içeriği silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return <div className="p-3 pb-20 sm:p-5 lg:p-6 max-w-[1200px]">
    <div className="flex items-center gap-3 mb-3 sm:mb-5"><div className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center bg-pleiades-blue text-white"><Library size={18} /></div><div><h1 className="text-lg sm:text-xl font-semibold">Ses ve Meditasyon Kütüphanesi</h1><p className="text-[11px] sm:text-[12px] text-pleiades-slate mt-0.5 sm:mt-1">Meditasyon, müzik ve eğitim seslerini kullanıcı gruplarına yayınlayın.</p></div></div>

    <PortalModuleToggle
      moduleKey="audio_enabled"
      title="Sesler sekmesi"
      description="Pasif olduğunda ses dosyaları korunur; danışan menüsü, ana sayfa kartı, oynatma ve indirme erişimi kapanır."
    />

    <form onSubmit={submit} className="bg-white border border-pleiades-border p-3 sm:p-5 mb-3 sm:mb-5">
      {editingId && <div className="mb-4 flex items-center justify-between border border-pleiades-blue/30 bg-pleiades-blue-soft px-3 py-2 text-[12px] text-pleiades-blue"><span>Yayınlanan ses içeriğini düzenliyorsunuz.</span><button type="button" onClick={cancelEdit} className="grid h-9 w-9 place-items-center" aria-label="Düzenlemeyi kapat"><X size={16} /></button></div>}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="İçeriğin adı"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={180} className="input" placeholder="Örn. İçsel Denge Meditasyonu" /></Field>
        <Field label="Konu başlığı"><input required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} maxLength={120} className="input" placeholder="Örn. İçsel Denge" /></Field>
        <Field label="İçerik türü"><select value={form.audio_category} onChange={(e) => setForm({ ...form, audio_category: e.target.value })} className="input">{Object.entries(categories).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></Field>
      </div>
      <div className="mt-4"><AudienceSelector value={form} onChange={setForm} clients={clients} /></div>
      <div className="mt-4"><Field label="Açıklama / anlatım"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} maxLength={3000} className="input py-3 resize-y" placeholder="İçeriğin amacı ve dinleme önerileri…" /></Field></div>
      <div className="mt-4 grid md:grid-cols-[1fr_auto] gap-3 items-end">
        <Field label={editingId ? "Yeni ses dosyası (isteğe bağlı)" : "Ses dosyası (en fazla 100 MB)"}><input required={!editingId} type="file" accept=".mp3,.m4a,.wav,.ogg,.aac,.flac,audio/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="block w-full text-[12px] file:min-h-10 file:px-4 file:border-0 file:bg-pleiades-ivory file:mr-3" /></Field>
        <button disabled={busy} className="fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-[4.25rem] right-3 z-20 min-h-12 px-5 bg-pleiades-blue text-white text-[12px] flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 sm:static sm:min-h-11 sm:shadow-none"><Upload size={15} />{busy ? "Kaydediliyor…" : editingId ? "Değişiklikleri kaydet" : "Yükle ve yayınla"}</button>
      </div>
    </form>

    <div className="bg-white border border-pleiades-border">
      <div className="px-5 py-4 border-b border-pleiades-border text-[13px] font-semibold">Yayınlanan içerikler · {items.length}</div>
      {items.length ? items.map((item) => <div key={item.id} className="px-5 py-4 border-b last:border-b-0 border-pleiades-border flex items-start gap-4">
        <div className="w-10 h-10 grid place-items-center bg-pleiades-ivory shrink-0"><Headphones size={17} /></div>
        <div className="min-w-0 flex-1"><div className="text-[10px] uppercase tracking-wider text-pleiades-blue">{item.topic} · {categories[item.audio_category]}</div><div className="text-[13px] font-semibold mt-1">{item.title}</div><p className="text-[11px] text-pleiades-slate mt-1 line-clamp-2">{item.description || "Açıklama eklenmedi."}</p><div className="text-[10px] text-pleiades-muted mt-2">{audienceLabel(item, clients)} · {Math.ceil((item.size || 0) / 1024 / 1024)} MB</div></div>
        <div className="flex shrink-0"><button onClick={() => edit(item)} aria-label="Ses içeriğini düzenle" className="w-9 h-9 grid place-items-center text-pleiades-muted hover:text-pleiades-blue"><Pencil size={15} /></button><button onClick={() => remove(item)} aria-label="Ses içeriğini sil" className="w-9 h-9 grid place-items-center text-pleiades-muted hover:text-red-700"><Trash2 size={15} /></button></div>
      </div>) : <div className="p-12 text-center text-[12px] text-pleiades-muted">Henüz ses içeriği yüklenmedi.</div>}
    </div>
  </div>;
}

function Field({ label, children }) { return <label className="block"><span className="label">{label}</span>{children}</label>; }
